import { create } from 'zustand'
import { apiService } from '../services/apiService'
import { getAuthMode, getEnvironment } from '../lib/env'
import { sessionStorage } from '../services/api/sessionStorage'

interface AuthState {
  isAuthenticated: boolean
  environment: 'staging' | 'production'
  apiType: 'bearer' | 'token'
  authMode: 'AUTH0' | 'API_KEY'
  loading: boolean
  error: string | null

  // Actions
  login: (apiKey: string, type?: 'bearer' | 'token') => Promise<boolean>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  setEnvironment: (env: 'staging' | 'production') => Promise<void>
  setAuthMode: (mode: 'AUTH0' | 'API_KEY') => void
  clearError: () => void
}

// If a session is already stored (e.g. new tab), start in loading state so
// ProtectedRoute shows a spinner while checkAuth() validates the token,
// instead of immediately redirecting to /login.
const hasStoredSession = sessionStorage.isAuthenticated()

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  environment: getEnvironment(),
  apiType: 'bearer',
  authMode: getAuthMode(),
  loading: hasStoredSession,
  error: null,

  login: async (apiKey: string, type: 'bearer' | 'token' = 'bearer') => {
    set({ loading: true, error: null })
    try {
      const response = await apiService.auth.login(apiKey, type)
      if (response.success) {
        set({
          isAuthenticated: true,
          apiType: type,
          environment: response.data?.config?.environment || 'staging',
          loading: false,
        })
        return true
      } else {
        set({ error: response.error || 'Login failed', loading: false })
        return false
      }
    } catch (error: any) {
      set({ error: error.message || 'Login failed', loading: false })
      return false
    }
  },

  logout: async () => {
    set({ loading: true })
    try {
      await apiService.auth.logout()
      set({
        isAuthenticated: false,
        loading: false,
        error: null,
      })
    } catch (error: any) {
      set({ error: error.message || 'Logout failed', loading: false })
    }
  },

  checkAuth: async () => {
    set({ loading: true })
    try {
      const response = await apiService.auth.check()
      if (response.data?.isAuthenticated) {
        set({
          isAuthenticated: true,
          environment: response.data?.config?.environment || 'staging',
          apiType: response.data?.config?.apiType || 'bearer',
          loading: false,
        })
      } else {
        set({ isAuthenticated: false, loading: false })
      }
    } catch (error) {
      set({ isAuthenticated: false, loading: false })
    }
  },

  setEnvironment: async (env: 'staging' | 'production') => {
    set({ loading: true, error: null })
    try {
      const response = await apiService.auth.setEnvironment(env)
      if (response.success) {
        set({ environment: env, loading: false })
      } else {
        set({ error: response.error || 'Failed to change environment', loading: false })
      }
    } catch (error: any) {
      set({ error: error.message || 'Failed to change environment', loading: false })
    }
  },

  clearError: () => set({ error: null }),

  setAuthMode: (mode: 'AUTH0' | 'API_KEY') => {
    set({ authMode: mode })
    // Store preference in localStorage
    try {
      localStorage.setItem('auth_mode_preference', mode)
    } catch (error) {
      console.error('[AuthStore] Failed to store auth mode preference:', error)
    }
  },
}))
